<script setup lang="ts">
import { useDateFormat } from '@vueuse/core';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  date: {
    type: String,
    default: '',
  },
  file: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showActions: {
    type: Boolean,
    default: false,
  },
  hideVersion: {
    type: Boolean,
    default: false,
  },
});

const route = useRoute();
const termsStore = useTermsStore();

const linksFromApi = computed<TermsLink[]>(() => {
  return termsStore.termsLinks;
});

const hasLoadedTerms = ref(false);

const loadTermsLazily = async () => {
  if (!hasLoadedTerms.value && !linksFromApi.value.length) {
    hasLoadedTerms.value = true;
    await termsStore.getCurrentLangTermsPlusDefaultTerms('pt-br');
  }
};

function onPrint() {
  window.print();
}

const formatted_date = computed(() => {
  const dateArray = props.date?.split(' ')[0]?.split('/');
  const time = props.date?.split(' ')[1];

  const day = dateArray?.[0] ?? '';
  const month = dateArray?.[1] ?? '';
  const year = dateArray?.[2] ?? '';
  const value = `${year}/${month}/${day} ${time}`;

  return useDateFormat(value, 'DD MMM YYYY').value;
});
</script>

<template>
  <div class="pageContainer">
    <div class="boxesWrapper">
      <div class="box">
        <BaseCommonMainTitle
          class="mb-3"
          tag="h1"
        >
          <div
            v-if="props.isLoading"
            class="skeleton-loading h-5 w-32 lg:h-6"
          ></div>
          <span v-else>
            {{ $t(props.title) }}
          </span>
        </BaseCommonMainTitle>

        <div
          v-if="!hideVersion || showActions"
          class="boxSection"
        >
          <template v-if="props.isLoading">
            <div class="skeleton-loading h-6 w-64"></div>
            <div class="skeleton-loading h-7 w-36"></div>
          </template>
          <template v-else>
            <div
              v-if="!hideVersion"
              class="boxSectionVersion"
            >
              <div class="flex items-center gap-2">
                <NuxtIcon name="file" />
                <b>{{ $t('terms.version') }}:</b>
                1.0
              </div>
              <div
                v-if="props.date"
                class="flex items-center gap-2"
              >
                <NuxtIcon name="calendar" />
                <b>{{ $t('terms.date') }}:</b>
                <span>{{ formatted_date }}</span>
              </div>
            </div>

            <div
              v-if="showActions"
              class="boxSectionAction"
            >
              <a
                v-if="props.file"
                class="text-primary bg-primary/10"
                download
                :href="props.file"
              >
                {{ $t('terms.download') }}
              </a>

              <button
                class="text-texts/60"
                @click="onPrint"
              >
                {{ $t('terms.print') }}
              </button>
            </div>
          </template>
        </div>

        <div class="pageSlotWrapper">
          <slot />
        </div>
      </div>

      <div
        v-if="route.path != '/sponsors' && linksFromApi.length"
        class="pageSidebar"
        @mouseenter="loadTermsLazily"
      >
        <RouterLink
          v-for="link in linksFromApi"
          :key="link.route"
          active-class="opacity-100"
          class="menuLinks"
          :to="localeLink(link.route)"
        >
          {{ $t(link.title) }}
        </RouterLink>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.pageContainer {
  @apply flex flex-col w-full mb-4 lg:mb-10 text-texts/70 text-sm mt-0;
}

.pageSlotWrapper {
  ::v-deep(p),
  ::v-deep(h2) {
    @apply mb-4 leading-6;
  }

  ::v-deep(h2),
  ::v-deep(h3) {
    @apply text-base text-texts;
  }

  ::v-deep(h3) {
    @apply text-sm;
  }

  ::v-deep(h2),
  ::v-deep(h3),
  ::v-deep(h4) {
    @apply font-semibold;
  }

  ::v-deep(ol) {
    padding: 0;
    counter-reset: item;
    list-style-type: none;

    li > ol {
      @apply ml-4;
    }

    h3,
    li {
      @apply mb-4;
    }
  }

  ::v-deep(li) {
    counter-increment: item;

    h3,
    p {
      display: inline;
    }

    &:before {
      content: counters(item, '.') ' - ';
    }
  }
}

.boxesWrapper {
  @apply flex flex-col flex-wrap justify-between items-start gap-6 lg:flex-row;

  .box {
    @apply bg-footer-bg-secondary gap-2 m-0 p-6 rounded-md;
    @apply w-full lg:w-auto flex-1;
    .boxSection {
      @apply text-texts relative py-4 mb-4 flex flex-col gap-4 justify-between lg:flex-row;

      &:before,
      &:after {
        @apply content-[''] w-full h-px bg-texts absolute left-0 opacity-5;
      }
      &:before {
        @apply top-0;
      }
      &:after {
        @apply bottom-0;
      }

      button,
      a {
        @apply px-2 py-1 rounded text-sm;
      }

      .boxSectionVersion {
        @apply flex gap-5 text-sm;
      }

      .boxSectionAction {
        @apply flex flex-row items-center gap-2 lg:flex-row-reverse;

        @media print {
          & {
            display: none;
          }
        }
      }
    }
  }

  .pageSidebar {
    @apply bg-footer-bg-secondary text-white p-5 py-4 rounded-md text-base gap-2 m-0 w-full lg:w-auto flex-1 lg:max-w-[250px];

    .menuLinks {
      @apply px-2 py-2.5 flex hover:opacity-100 uppercase text-xs opacity-60;
    }
  }
}
</style>

<style lang="postcss">
.pageSlotWrapper {
  p,
  span,
  strong,
  b,
  ul,
  em {
    @apply text-texts/70 !important;
    @apply bg-transparent !important;
    @apply mb-5 !important;
  }

  em,
  b,
  strong {
    @apply font-bold !important;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    @apply font-bold text-base;
    @apply mb-5 !important;
  }

  h3,
  h4,
  h5,
  h6 {
    @apply text-sm;
  }

  table {
    @apply w-full border-collapse mb-5 border border-solid border-texts/10;

    thead {
      @apply bg-texts/5;
    }

    tr:nth-child(odd) {
      @apply bg-texts/5;
    }

    tr:nth-child(even) {
      @apply bg-texts/0;
    }

    th,
    td {
      @apply py-3 px-4 text-left;

      p {
        @apply mb-0.5 !important;
        @apply text-xs !important;
      }
    }
  }
}
</style>

<style lang="css">
@media print {
  html {
    overflow: visible !important;
    background-color: unset !important;
    color: black !important;
  }
  .pageContainer,
  .boxesWrapper,
  .pageContainer .boxesWrapper * {
    color: black !important;
    background-color: white !important;
  }
  .pageSidebar {
    display: none !important;
  }
}
</style>
